import { useState } from "react";
import {
  Moon,
  Sun,
  Monitor,
  Clock,
  Bell,
  Lock,
  LogOut,
  Check,
  Download,
  RefreshCw,
} from "lucide-react";
import { usePwa } from "../pwa";
import { useStore } from "../store";
import { api } from "../api";
import { brand, type Settings as Preferences } from "../../shared/domain";
import { TimezoneSelector } from "../components/TimezoneSelector";
import { Modal, ErrorText } from "../components/ui";
export function Settings() {
  const { data, refresh, signOut, clear, offline } = useStore();
  const s = data!.settings;
  const pwa = usePwa();
  const ios =
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [password, setPassword] = useState(false),
    [permission, setPermission] = useState(
      "Notification" in window ? Notification.permission : "unsupported",
    ),
    [info, setInfo] = useState("");
  async function update(p: Partial<Preferences>) {
    if (offline) return;
    setBusy(true);
    setError("");
    try {
      await api("/settings", "PUT", { ...s, ...p });
      await refresh();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  }
  async function enablePush() {
    setError("");
    try {
      if (!("Notification" in window) || !("PushManager" in window))
        throw Error(
          "Web Push is not supported in this browser. On iOS, install the PWA to your Home Screen first.",
        );
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p !== "granted")
        throw Error(
          "Notification permission was not granted. Change it in your browser settings.",
        );
      const { key } = await api<{ key: string | null }>("/push/key");
      if (!key)
        throw Error(
          "Local push keys are missing. Run npm run push:keys and restart the Worker.",
        );
      const reg = await navigator.serviceWorker.ready;
      let subscription = await reg.pushManager.getSubscription();
      if (!subscription) {
        const bytes = Uint8Array.from(
          atob(key.replace(/-/g, "+").replace(/_/g, "/")),
          (c) => c.charCodeAt(0),
        );
        subscription = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: bytes,
        });
      }
      await api("/push", "POST", subscription.toJSON());
      setInfo("This browser is registered for daily summaries.");
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <>
      <div className="page-heading">
        <div>
          <p className="eyebrow">MAKE IT YOURS</p>
          <h1>
            Settings<span className="accent">.</span>
          </h1>
        </div>
      </div>
      <ErrorText text={error} />
      {info && (
        <p className="success" role="status">
          {info}
        </p>
      )}
      <fieldset disabled={offline || busy} className="settings-grid">
        <section className="settings-section">
          <h2>Appearance</h2>
          <p className="muted">Your space, your light.</p>
          <div className="theme-options">
            {(
              [
                { value: "dark", label: "Dark", Icon: Moon },
                { value: "light", label: "Light", Icon: Sun },
                { value: "system", label: "System", Icon: Monitor },
              ] as const
            ).map(({ value, label, Icon }) => (
              <button
                aria-pressed={s.theme === value}
                key={value}
                className={s.theme === value ? "selected" : ""}
                onClick={() => void update({ theme: value })}
              >
                <Icon size={21} />
                {label}
                {s.theme === value && <Check size={16} />}
              </button>
            ))}
          </div>
        </section>
        <section className="settings-section">
          <h2>
            <Clock size={17} />
            Calendar preferences
          </h2>
          <label>
            Time format
            <select
              value={s.timeFormat}
              onChange={(e) =>
                void update({ timeFormat: e.target.value as "12" | "24" })
              }
            >
              <option value="12">12-hour (AM/PM)</option>
              <option value="24">24-hour</option>
            </select>
          </label>
          <DurationPreference
            duration={s.defaultDuration}
            onSave={(value) => void update({ defaultDuration: value })}
          />
          <TimezoneSelector
            value={s.timezone}
            onChange={async (timezone) => {
              if (!(await update({ timezone })))
                throw new Error("Timezone was not saved");
            }}
          />
        </section>
        <section className="settings-section">
          <h2>
            <Bell size={17} />
            Daily summary
          </h2>
          <div className="setting-row">
            <div>
              <strong>Daily reminder</strong>
              <p className="muted">One gentle heads-up for your day.</p>
            </div>
            <button
              role="switch"
              aria-checked={s.notifications}
              aria-label="Daily reminder"
              className={"switch " + (s.notifications ? "on" : "")}
              onClick={() => void update({ notifications: !s.notifications })}
            >
              <span />
            </button>
          </div>
          <label>
            Reminder time
            <input
              type="time"
              value={s.reminderTime}
              onChange={(e) => {
                if (e.target.value)
                  void update({ reminderTime: e.target.value });
              }}
            />
          </label>
          <p className="note">
            Before noon, your summary covers today. From noon onward, it covers
            tomorrow. Empty days stay quiet.
          </p>
          <div className="permission">
            <span>Browser permission</span>
            <strong>{permission}</strong>
          </div>
          <button className="secondary wide" onClick={() => void enablePush()}>
            Enable on this browser
          </button>
        </section>
        <section className="settings-section">
          <h2>
            <Lock size={17} />
            Account
          </h2>
          <p className="muted">Signed in as {data!.username}</p>
          <button className="settings-action" onClick={() => setPassword(true)}>
            <Lock size={16} />
            Change password
          </button>
          <button
            className="settings-action danger"
            onClick={() => void signOut().catch((e) => setError(e.message))}
          >
            <LogOut size={16} />
            Sign out
          </button>
          <h2 className="subheading">Application</h2>
          <p className="muted">{brand.name} · Appointmently V1</p>
          <button
            className="settings-action"
            onClick={() => {
              void navigator.serviceWorker
                ?.getRegistration()
                .then((r) => r?.update());
              setInfo(
                "Update check requested. If an update is ready, a banner will appear.",
              );
            }}
          >
            <RefreshCw size={16} />
            Check for updates
          </button>
          <div className="installation">
            <h3>
              <Download size={16} />
              {pwa.installed
                ? "Installed application"
                : "Install Appointmently"}
            </h3>
            {pwa.installed ? (
              <p className="muted">
                {pwa.standalone
                  ? "You're using Appointmently in standalone mode."
                  : "Open Appointmently from your Home Screen for the standalone experience."}
              </p>
            ) : (
              <>
                <p className="muted">
                  Keep your planner a tap away on your Home Screen.
                </p>
                {pwa.canInstall ? (
                  <button
                    className="secondary wide"
                    onClick={() => void pwa.install()}
                  >
                    Install application
                  </button>
                ) : (
                  <p className="note">
                    {ios
                      ? "In Safari, tap Share, then Add to Home Screen. Open the new Appointmently icon."
                      : "Use your browser menu and choose Install app or Add to Home Screen when available."}
                  </p>
                )}
              </>
            )}
            <ErrorText text={pwa.error} />
          </div>
        </section>
      </fieldset>
      {password && (
        <PasswordModal onClose={() => setPassword(false)} onComplete={clear} />
      )}
    </>
  );
}
function PasswordModal({
  onClose,
  onComplete,
}: {
  onClose: () => void;
  onComplete: () => void;
}) {
  const [current, setCurrent] = useState(""),
    [password, setPassword] = useState(""),
    [confirm, setConfirm] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  return (
    <Modal title="Change password" onClose={onClose}>
      <form
        className="form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (password !== confirm) {
            setError("New passwords do not match.");
            return;
          }
          setBusy(true);
          try {
            await api("/password", "POST", { current, password });
            onComplete();
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label>
          Current password
          <input
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </label>
        <label>
          New password
          <input
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <small className="muted">
          At least 12 characters. All sessions will be signed out.
        </small>
        <label>
          Confirm password
          <input
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        <ErrorText text={error} />
        <button className="primary wide" disabled={busy}>
          {busy ? "Updating…" : "Update password"}
        </button>
      </form>
    </Modal>
  );
}

function DurationPreference({
  duration,
  onSave,
}: {
  duration: number;
  onSave: (duration: number) => void;
}) {
  const [hours, setHours] = useState(Math.floor(duration / 60));
  const [minutes, setMinutes] = useState(duration % 60);
  const total = hours * 60 + minutes;
  return (
    <div className="duration-preference">
      <span>Default appointment duration</span>
      <div className="form-row">
        <label>
          Hours
          <input
            aria-label="Default duration hours"
            type="number"
            min="0"
            max="168"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
          />
        </label>
        <label>
          Minutes
          <input
            aria-label="Default duration minutes"
            type="number"
            min="0"
            max="59"
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
          />
        </label>
      </div>
      {total !== duration && (
        <button
          className="secondary wide"
          disabled={
            total < 1 ||
            total > 10080 ||
            minutes < 0 ||
            minutes > 59 ||
            hours < 0 ||
            !Number.isInteger(total)
          }
          onClick={() => onSave(total)}
        >
          Save default duration
        </button>
      )}
    </div>
  );
}
